import React, { useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import classnames from "classnames";

import AppRadioBtn from "@vahak/core/dist/components/app-radio-btn/AppRadioBtn";
import { CitySearchResultWithCateg } from "@vahak/core/dist/custom-types/location-search";
import { InputChangeEventHandler } from "@vahak/core/dist/custom-types/handler";
import SearchBlueIcon from "@vahak/core/dist/icons/SearchBlue.svg";
import GreyPlusIcon from "@vahak/core/dist/icons/GreyPlus.svg";
import CheckBlackIcon from "@vahak/core/dist/icons/CheckBlack.svg";
import CloseCircleIcon from "@vahak/core/dist/icons/CloseCircle.svg";

import Styles from "./EditSearchModal.module.scss";
import QuickFilterPill from "../quick-filter-pill/QuickFilterPill";
import AppBtn from "@vahak/core/dist/components/app-btn/AppBtn";
import AppInput from "@vahak/core/dist/components/app-input/AppInput";
import { getRemValue } from "@vahak/core/dist/methods/getRemValue";
import { FONT_WEIGHTS } from "@vahak/core/dist/constants/misc";
import CloseBtnStyles from "../CloseBtn.module.scss";

export interface EditSearchModalProps {
    isVisible: boolean;
    handleToggle: () => void;
    nameSearchVal: string;
    onInputNameSearch: InputChangeEventHandler;
    onClearName?: () => void;
    sourceSearchLabelKey: (option: any) => string;
    selectedSource: CitySearchResultWithCateg | undefined;
    onClearSrc?: () => void;
    onClickSearchSource: () => void;
    topDests: CitySearchResultWithCateg[];
    selectedDest: CitySearchResultWithCateg | undefined;
    onClickDestQuickFilter: (state: CitySearchResultWithCateg) => void;
    onClickDestSearchBtn: () => void;
    onDeselectDestSearchBtn: () => void;
    onClickUpdateSearch: () => void;
    searchBy?: "location" | "name";
    onChangeSearchBy: InputChangeEventHandler;
}

const EditSearchModal = ({
    isVisible,
    handleToggle,
    nameSearchVal,
    onInputNameSearch,
    onClearName,
    sourceSearchLabelKey,
    selectedSource,
    onClearSrc,
    onClickSearchSource,
    topDests,
    selectedDest,
    onClickDestQuickFilter,
    onDeselectDestSearchBtn,
    onClickDestSearchBtn,
    onClickUpdateSearch,
    searchBy,
    onChangeSearchBy
}: EditSearchModalProps) => {
    // const location = useLocation();

    const _onClearSrc = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.stopPropagation();
        onClearSrc?.();
    };

    const _onClearName = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.stopPropagation();
        onClearName?.();
    };

    // useEffect(() => {
    //     location.state = undefined;
    // }, []);

    useEffect(() => {
        (async () => {
            if (isVisible) {
                document.body.style.overflow = "hidden";
                if (window.scrollY !== 0) {
                    window.scrollTo({ left: 0, top: 0, behavior: "auto" });
                }
                (document.querySelector(".sticky-inner-wrapper") as HTMLDivElement).classList.add("inactive");
            } else {
                document.body.style.overflow = "unset";
                (document.querySelector(".sticky-inner-wrapper") as HTMLDivElement).classList.remove("inactive");
            }
        })();
    }, [isVisible]);

    return (
        <Modal
            isOpen={isVisible}
            backdrop
            keyboard
            unmountOnClose
            toggle={handleToggle}
            className={Styles.container}
            modalClassName="EditSearchModal__modal"
            contentClassName={Styles.content}
        >
            <ModalBody className={Styles.body}>
                <div className={Styles.searchBy}>
                    <div className={Styles.label}>Search Transporters</div>
                    <div className={Styles.searchByRadioBtns}>
                        <AppRadioBtn
                            labelText="By Location"
                            name="serachBy"
                            value={"location"}
                            isChecked={searchBy === "location"}
                            className={Styles.locationRadioBtn}
                            inputClassName={Styles.radioBtn__input}
                            labelClassName={Styles.radioBtn__label}
                            onChange={onChangeSearchBy}
                        />
                        <AppRadioBtn
                            labelText="By Name"
                            name="serachBy"
                            value="name"
                            isChecked={searchBy === "name"}
                            className={Styles.nameRadioBtn}
                            inputClassName={Styles.radioBtn__input}
                            labelClassName={Styles.radioBtn__label}
                            onChange={onChangeSearchBy}
                        />
                    </div>
                </div>
                {searchBy === "location" && (
                    <>
                        <div className={Styles.src}>
                            <div className={Styles.label}>Source Location</div>
                            <div
                                className={classnames(Styles.srcSearchBtn__Text, !selectedSource && Styles.placeholder)}
                                onClick={onClickSearchSource}
                            >
                                {(selectedSource && (
                                    <>
                                        {sourceSearchLabelKey(selectedSource)}{" "}
                                        <span className={CloseBtnStyles.btn} onClick={_onClearSrc}>
                                            <CloseCircleIcon />
                                        </span>
                                    </>
                                )) ||
                                    "Where is your Loading Point?"}
                            </div>
                        </div>
                        <div className={Styles.dest}>
                            <div className={Styles.label}>Destination State</div>
                            <div className={Styles.dest_search_control}>
                                {topDests.slice(0, 2).map((state) => (
                                    <QuickFilterPill
                                        key={state?.state_id}
                                        text={state?.state}
                                        suffix={
                                            state?.state_id === selectedDest?.state_id ? (
                                                <CheckBlackIcon />
                                            ) : (
                                                <GreyPlusIcon />
                                            )
                                        }
                                        className={Styles.dest__quick_filter}
                                        isMobile={true}
                                        isSelected={state?.state_id === selectedDest?.state_id}
                                        onSelected={onClickDestQuickFilter}
                                        onDeselected={onDeselectDestSearchBtn}
                                        data={state}
                                    />
                                ))}
                                <QuickFilterPill
                                    prefix={<SearchBlueIcon height={12} width={12} />}
                                    className={Styles.search_icon_container}
                                    prefixClassName={Styles.search_icon}
                                    onSelected={onClickDestSearchBtn}
                                    isMobile={true}
                                />
                            </div>
                        </div>
                    </>
                )}
                {searchBy === "name" && (
                    <div className={Styles.nameSearch}>
                        <div className={Styles.label}>Transporter Name</div>
                        <div className={Styles.nameSearch__Container}>
                            <AppInput
                                isActiveByDefault
                                labelText=""
                                value={nameSearchVal}
                                name="searchByName"
                                type="text"
                                onChange={onInputNameSearch}
                                containerStyles={{ border: "none", height: "auto" }}
                                inputWrapperStyles={{ padding: 0 }}
                                inputStyles={{
                                    color: "#1053FF",
                                    fontSize: getRemValue(16),
                                    letterSpacing: getRemValue(-0.16),
                                    fontWeight: FONT_WEIGHTS.medium,
                                    lineHeight: getRemValue(20),
                                    padding: 0
                                }}
                                autoComplete={false}
                            />
                            {nameSearchVal?.trim() ? (
                                <span className={CloseBtnStyles.btn} onClick={_onClearName}>
                                    <CloseCircleIcon />
                                </span>
                            ) : (
                                <QuickFilterPill
                                    prefix={<SearchBlueIcon height={12} width={12} />}
                                    className={Styles.search_icon_container}
                                    prefixClassName={Styles.search_icon}
                                    isMobile={true}
                                />
                            )}
                        </div>
                    </div>
                )}
                <AppBtn
                    className={Styles.submitBtn}
                    isMobile
                    width="140px"
                    onClick={onClickUpdateSearch}
                    // disabled={searchBy === "name" && !nameSearchVal?.trim()}
                >
                    Update Search
                </AppBtn>
            </ModalBody>
        </Modal>
    );
};

export default EditSearchModal;
